import { getApiConfig } from "../config";
import { createHttpError } from "../errors";
import { AttributionApi, AuthenticationApi, CancellationApi, ContactApi, DefaultApi, PaymentsApi, PurchasesApi, QuotaApi, SessionApi, SubscriptionsApi, VisitsApi, } from "./generated/apis";
import * as runtime from "./generated/runtime";
export class V2mApiClient {
    baseUrl;
    onAuthError;
    constructor(config = {}) {
        this.baseUrl = config.baseUrl ?? getApiConfig().baseUrl;
    }
    setAuthErrorHandler(handler) {
        this.onAuthError = handler;
    }
    auth = {
        me: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).authentication.getUserInfoV1AuthMeGet());
        },
        register: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).authentication.registerUserV1AuthRegisterPost());
        },
        deleteUser: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.deleteUserV1UserMeDelete());
        },
    };
    user = {
        profile: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.getUserProfileV1UserProfileGet());
        },
        updateProfile: async (idToken, updates) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.updateUserProfileV1UserProfilePatch({
                updateProfileRequest: updates,
            }));
        },
        preferences: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.getUserPreferencesV1UserPreferencesGet());
        },
        updatePreferences: async (idToken, updates) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.updateUserPreferencesV1UserPreferencesPost({
                updatePreferencesRequest: updates,
            }));
        },
        // Commercial-email (newsletter/marketing) consent, art. 398 ust. 1 PKE
        // (V2M-DEV-314). NOT the cookie-banner's `marketingConsented` flag — see
        // `CommercialEmailConsent`'s own generated docstring for why the two
        // must never be confused. `getCommercialEmailConsent` backs the
        // account/settings withdrawal toggle (criterion 3: withdrawal must be
        // at least as easy as granting); `updateCommercialEmailConsent` is
        // called both there and right after sign-in for a visitor who checked
        // WelcomePage's consent checkbox (see x2mail_app's
        // `emailConsentGrant.ts` — this origin has no authenticated session to
        // call it from directly).
        getCommercialEmailConsent: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.getCommercialEmailConsentV1UserCommercialEmailConsentGet());
        },
        updateCommercialEmailConsent: async (idToken, request) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.updateCommercialEmailConsentV1UserCommercialEmailConsentPost({
                updateCommercialEmailConsentRequest: request,
            }));
        },
        // Browser-extension telemetry consent (V2M-DEV-338) — a SEPARATE consent
        // from the one above: NOT the cookie-banner's `CookieConsent.analytics`
        // bucket (website measurement) and NOT `commercialEmailConsent` (email).
        // Three states, never a boolean — see `ExtensionTelemetryConsent`'s own
        // generated docstring. `getExtensionTelemetryConsent` backs Kanał B (the
        // extension service worker's backend read); `updateExtensionTelemetryConsent`
        // is called from the account/settings page and — pre-login — indirectly
        // via /welcome's second consent checkbox handoff (V2M-DEV-335 pas).
        getExtensionTelemetryConsent: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.getExtensionTelemetryConsentV1UserExtensionTelemetryConsentGet());
        },
        updateExtensionTelemetryConsent: async (idToken, request) => {
            return await this.wrap(() => this.withAuth(idToken).defaultApi.updateExtensionTelemetryConsentV1UserExtensionTelemetryConsentPost({
                updateExtensionTelemetryConsentRequest: request,
            }));
        },
    };
    session = {
        init: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).sessionApi.sessionInitV1SessionInitGet());
        },
    };
    billing = {
        quota: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).quota.getQuotaV1QuotaGet());
        },
        usageHistory: async (idToken, limit = 50) => {
            return await this.wrap(() => this.withAuth(idToken).quota.getUsageHistoryV1QuotaHistoryGet({ limit }));
        },
        subscription: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).subscriptions.getSubscriptionV1SubscriptionsGet());
        },
        checkoutSession: async (idToken, tier, successUrl, cancelUrl, consentTimestamps, 
        // Customer's explicit payment-provider choice (V2M-235, payment-method
        // selector on the pricing page). Omitted entirely (not sent as `null`)
        // when absent, so the backend's `provider: ProviderId | None = None`
        // default — "use the market's primary provider, unchanged" — is
        // reached the exact same way a pre-V2M-235 request body would.
        //
        // No `currency` parameter (removed 2026-07-29, PK — "Kasujemy
        // swobodny selektor waluty."): the request body's `currency` field
        // still exists and is validated server-side (`CheckoutRequest.
        // currency: Currency | None = None`), but no frontend caller may ever
        // choose it anymore — the backend always resolves it from the
        // caller's IP. Sending a client-guessed currency here would be the
        // exact arbitrage hole PK's directive closes (a Polish customer
        // picking EUR overpaid ~72% relative to the PLN price).
        provider, 
        // No `returnUrl` parameter (removed 2026-08-05 with checkout contract
        // v4): that field only ever existed for Stripe's embedded `ui_mode`, and
        // the backend's `CheckoutRequest` no longer has it. The
        // `session_id={CHECKOUT_SESSION_ID}` token the return page needs is
        // appended SERVER-SIDE, inside the Stripe-only path
        // (`stripe_checkout_service.with_stripe_checkout_session_id`) — never
        // baked into `successUrl` here, which PayPal and Mercado Pago copy into
        // their own redirect verbatim and would send a paying customer to a URL
        // carrying the literal, unsubstituted token.
        // V2M-LEGAL-009 — same coordinate, same reasoning as
        // `purchases.checkout`'s own `legalDocumentVersions` below. LAST
        // positional arg so every existing call site keeps working unchanged.
        // snake_case on the wire here (this endpoint's body is hand-built and
        // snake_case throughout), camelCase on the pack endpoint — each matches
        // its own contract, both validated by the same backend gate.
        legalDocumentVersions) => {
            return await this.wrap(async () => {
                const body = {
                    tier,
                    success_url: successUrl,
                    cancel_url: cancelUrl,
                };
                if (legalDocumentVersions) {
                    body.legal_document_versions = legalDocumentVersions;
                }
                if (consentTimestamps?.tosAcceptedAt) {
                    body.tos_accepted_at = consentTimestamps.tosAcceptedAt;
                }
                if (consentTimestamps?.withdrawalWaiverAt) {
                    body.withdrawal_waiver_at = consentTimestamps.withdrawalWaiverAt;
                }
                if (provider) {
                    body.provider = provider;
                }
                const response = await fetch(`${this.baseUrl}/v1/subscriptions/checkout`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${idToken}`,
                    },
                    body: JSON.stringify(body),
                });
                if (!response.ok) {
                    throw await createHttpError(response);
                }
                // Raw snake_case parse (this endpoint's response_model is a plain
                // `{url}` — see `src/api/subscriptions.py`'s `CheckoutSessionResponse`).
                // A body without `url` is a backend contract break: fail loud rather
                // than hand the caller an unusable `undefined` to redirect to.
                //
                // A `client_secret`-only body (a backend still on checkout contract
                // v2/v3, embedded Checkout) lands here too, deliberately — this build
                // cannot mount an embedded session, so "I parsed it" would be a lie
                // and the customer would sit on a dead confirm button. Throwing routes
                // it into the caller's error path, which reports it and tells the
                // customer. The deploy gate (scripts/_checkout_contract_gate.sh) is
                // what keeps that combination off production in the first place.
                const data = (await response.json());
                if (typeof data.url === "string") {
                    return { url: data.url };
                }
                if (typeof data.client_secret === "string") {
                    throw new Error("Checkout session response carried an embedded client_secret (backend checkout contract 2/3), which this build cannot mount — it only follows a hosted checkout url.");
                }
                throw new Error("Checkout session response had no redirect URL.");
            });
        },
        // Hosted Checkout return page: after Stripe redirects the browser back to
        // the session's `success_url?session_id=...`, the page asks the backend
        // what happened — the client can never retrieve a `checkout.Session`
        // itself (that needs the secret key). `GET /v1/subscriptions/checkout/
        // {session_id}` has a real `response_model` (unlike `/checkout`), so —
        // unlike `checkoutSession` above, which stays hand-rolled permanently —
        // this one goes through the generated `SubscriptionsApi` client, typed
        // (`CheckoutSessionStatusResponse`, already camelCase `paymentStatus`)
        // as soon as it landed (v2m-backend commit 912c60fe, 2026-08-04).
        checkoutSessionStatus: async (idToken, sessionId) => {
            return await this.wrap(async () => {
                const response = await this.withAuth(idToken).subscriptions.getCheckoutSessionStatusV1SubscriptionsCheckoutSessionIdGet({
                    sessionId,
                });
                // The generated model types status/paymentStatus as plain `string`
                // (OpenAPI gives the backend's Pydantic `str` fields no enum) —
                // narrowed here to the documented Stripe values so callers get an
                // exhaustive-switch-friendly type.
                return {
                    status: response.status,
                    paymentStatus: response.paymentStatus,
                    pending: response.pending,
                };
            });
        },
        customerPortalSession: async (idToken, returnUrl) => {
            const portalRequest = { returnUrl };
            return await this.wrap(async () => {
                const response = await this.withAuth(idToken).subscriptions.createCustomerPortalSessionV1SubscriptionsPortalPost({
                    portalRequest,
                });
                return response;
            });
        },
        // Upgrade a LIVE subscription to a higher tier — PK's own billing model
        // (2026-07-24): full new-tier price charged immediately, not Stripe
        // proration. The only sanctioned path for a tier change on an existing
        // subscription; a user without one uses `checkoutSession` above instead
        // (see that endpoint's 409 `ACTIVE_SUBSCRIPTION` contract for how a
        // repeat-checkout attempt is routed here). See
        // `src.services.subscription_upgrade_service` on the backend for the
        // full contract (idempotency, bonus-credit gift, billing-anchor reset).
        upgradeSubscription: async (idToken, tier) => {
            return await this.wrap(() => this.withAuth(idToken).subscriptions.upgradeSubscriptionEndpointV1SubscriptionsUpgradePost({
                upgradeSubscriptionRequest: { tier },
            }));
        },
        // Self-service subscription management (PK's own billing model,
        // 2026-07-24 10:29) — the dashboard's PlanManagement card. Cancellation
        // NEVER ends a subscription outright (only flips Stripe's own
        // `cancel_at_period_end` flag — see `src.services.subscription_
        // cancellation_service` on the backend, the P0 incident this closes);
        // downgrade NEVER uses Stripe proration (a Subscription Schedule defers
        // the lower price to the NEXT period — `src.services.subscription_
        // downgrade_service`). All four are idempotent server-side (a retry
        // returns `"already_processed"`/`"already_released"`, never a second
        // Stripe call) — callers don't need to guard against a double click.
        /** Schedule cancellation at the end of the current, already-paid period. Reversed by `reactivateSubscription`. */
        cancelAtPeriodEnd: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).subscriptions.cancelAtPeriodEndEndpointV1SubscriptionsCancelAtPeriodEndPost());
        },
        /** Undo a pending `cancelAtPeriodEnd` — 400 if the period already ended (nothing left to reactivate). */
        reactivateSubscription: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).subscriptions.reactivateEndpointV1SubscriptionsReactivatePost());
        },
        /**
         * Schedule a downgrade to a lower tier, effective at the end of the
         * current period. 409 (`TIER_NOT_LOWER` in the response body's `detail`)
         * when `tier` does not rank strictly lower than the caller's current
         * tier — the caller should route to the upgrade flow instead of
         * retrying this call.
         */
        scheduleDowngrade: async (idToken, tier) => {
            return await this.wrap(() => this.withAuth(idToken).subscriptions.downgradeSubscriptionEndpointV1SubscriptionsDowngradePost({
                downgradeSubscriptionRequest: { tier },
            }));
        },
        /** Cancel a pending downgrade — releases the Stripe Schedule, subscription stays on its current tier. */
        cancelDowngrade: async (idToken) => {
            return await this.wrap(() => this.withAuth(idToken).subscriptions.releaseDowngradeEndpointV1SubscriptionsDowngradeCancelPost());
        },
    };
    // One-time generation-pack top-ups ("credit packs" in pricing-page copy,
    // V2M payments wave 2 / Z4). Distinct from `billing` (subscriptions): mode
    // "payment", not "subscription" — see `src/api/purchases.py`. `packages()`
    // is public (no Firebase user needed to browse what's on offer); `checkout()`
    // requires auth, same as `billing.checkoutSession`.
    purchases = {
        packages: async () => {
            return await this.wrap(() => this.withoutAuth().purchasesApi.listPackagesV1PurchasesPackagesGet());
        },
        checkout: async (idToken, packageId, successUrl, cancelUrl, 
        // Both legally-required checkout consents, in ONE object rather than
        // two adjacent positional strings (V2M-LEGAL-003). Shape mirrors
        // `billing.checkoutSession`'s `consentTimestamps` above deliberately:
        // two same-typed ISO strings side by side are a silent swap waiting to
        // happen — nothing in the type system would catch a call passing the
        // waiver timestamp as the ToS one, and the resulting consent record
        // would be wrong in a way no test or gate could see.
        //
        // Both are `?`-optional in SHAPE only. The backend requires each of
        // them (`enforce_tos_consent` + `enforce_withdrawal_waiver_consent`,
        // `src/api/purchases.py`) and 400s before Stripe is touched if either
        // is missing/blank/unparseable — so an omitted field here is a loud
        // client failure, never a silent consent-free purchase.
        consentTimestamps, 
        // V2M-LEGAL-009: WHICH terms/privacy revision the customer was actually
        // shown. Travels from the client because the backend image does not
        // contain the legal documents at all (its Dockerfile copies only
        // `src/` + `scripts/`); the server validates the token's shape and
        // refuses malformed ones rather than inventing a version.
        legalDocumentVersions) => {
            const srcApiPurchasesCheckoutRequest = {
                packageId,
                successUrl,
                cancelUrl,
                tosAcceptedAt: consentTimestamps?.tosAcceptedAt,
                withdrawalWaiverAt: consentTimestamps?.withdrawalWaiverAt,
                legalDocumentVersions,
            };
            return await this.wrap(async () => {
                const response = await this.withAuth(idToken).purchasesApi.createCheckoutV1PurchasesCheckoutPost({
                    srcApiPurchasesCheckoutRequest,
                });
                return response;
            });
        },
    };
    // Public payment-methods listing (`GET /v1/payments/methods`, Faza 3c) — the
    // single source of truth for which payment methods a frontend may OFFER,
    // including PayPal's `settings.PAYPAL_ENABLED` kill switch and live
    // circuit-breaker health (P0 money-path audit, 2026-07-24/25). No auth
    // needed (public, no user-specific data) — same posture as
    // `purchases.packages` above. Added here (was previously generated but
    // never wired into this wrapper) so a frontend never has to hand-maintain
    // its own hardcoded payment-method list, which is exactly the kind of
    // second source of truth that let a disabled PayPal keep showing up.
    payments = {
        methods: async (market) => {
            return await this.wrap(() => this.withoutAuth().paymentsApi.getPaymentMethodsV1PaymentsMethodsGet({ market }));
        },
    };
    generation = {
    // Note: Deprecated endpoints (listModels, defaultPrompt, quickGenerateStream) removed
    // during API migration to unified /api/generate endpoint.
    // See docs/technical/GENERATION_API.md for migration guide.
    };
    contact = {
        submit: async (data) => {
            await this.wrap(async () => {
                await this.withoutAuth().contactApi.submitContactFormV1ContactPost({
                    contactRequest: data,
                });
            });
        },
    };
    // V2M-DEV-002 — "notify me when my market launches" waitlist. Both routes
    // are UNAUTHENTICATED by design: a visitor turned away by the
    // registration/checkout country whitelist never has an account to bear a
    // token with (see `AuthenticationApi`'s generated docstrings for the full
    // legal-basis reasoning) — same `withoutAuth()` posture as `contact` above.
    waitlist = {
        signup: async (data) => {
            return await this.wrap(() => this.withoutAuth().authentication.signupForMarketWaitlistV1AuthWaitlistPost({
                waitlistSignupRequest: data,
            }));
        },
        unsubscribe: async (email) => {
            return await this.wrap(() => this.withoutAuth().authentication.unsubscribeFromMarketWaitlistV1AuthWaitlistUnsubscribePost({ waitlistUnsubscribeRequest: { email } }));
        },
    };
    // Public contract-termination path (notes/intent/
    // 2026-07-29-v2m-publiczna-sciezka-rozwiazania-umowy.md, chapter 2).
    // Historically built to satisfy German § 312k BGB while DE was live; DE
    // is frozen since 2026-07-31 (ADR-0391) and kept as voluntary practice on
    // our live markets — PL carries no equivalent requirement, and neither
    // does EN WITHIN THE EU (docs/legal/
    // 2026-08-09-ocena-publicznej-sciezki-wypowiedzenia-312k.md §5). Outside
    // the EU the basis is UNVERIFIED (US FTC negative option and state law,
    // UK — never read at source) and `en` is `countries: []` in
    // `contracts/markets.yaml`, so non-EU customers already call these
    // endpoints; §5 records it as a NAMED OPEN GAP, a deliberate exception
    // rather than an oversight. Both
    // endpoints are unauthenticated by design (no `Depends(get_current_user)`
    // on the backend) — same `withoutAuth()` posture as `contact`/`visits`
    // above. The declaration text itself is delivered by email, not offered
    // for download here (PK 2026-08-14: an emailed contract already counts
    // as a durable medium) — the backend's document-download endpoint and
    // `documentToken` field were removed, and so was this wrapper's
    // `documentUrl` helper that used to build its URL.
    cancellation = {
        declare: async (data) => {
            return await this.wrap(() => this.withoutAuth().cancellationApi.declareCancellationEndpointV1CancellationDeclarePost({
                cancellationDeclareRequest: data,
            }));
        },
        revoke: async (data) => {
            return await this.wrap(() => this.withoutAuth().cancellationApi.revokeCancellationV1CancellationRevokePost({
                cancellationRevokeRequest: data,
            }));
        },
    };
    // Pre-login acquisition attribution (see src/install/, /welcome in
    // voice2mail_webpage).
    // No auth: both calls happen before the visitor has an account.
    visits = {
        trackVisit: async (payload) => {
            return await this.wrap(() => this.withoutAuth().visitsApi.trackVisitV1TrackVisitPost({ trackVisitRequest: payload }));
        },
        // `src` is optional and backward-compatible (Z1b, cookie-stitch on
        // install): forwarded straight from the welcome page's own `?src=`
        // query param. Only the literal value "install" makes the backend stamp
        // the install-stitch marker and report `stitched: true` in the response
        // — see WelcomePingRequest/WelcomePingResponse docstrings.
        welcomePing: async (visitToken, src) => {
            return await this.wrap(() => this.withoutAuth().visitsApi.welcomePingV1WelcomePingPost({
                welcomePingRequest: { visitToken, src: src ?? undefined },
            }));
        },
        // V2M-DEV-197: stateless, no-identifier counter for the "opened /welcome
        // with NO v2m_visit cookie" population — legal ruled that even a
        // server-side read of a click/visit identifier counts as "accessing" the
        // device under consent guidance, so this call takes zero arguments and
        // carries nothing that could be read as one. See WelcomePage.tsx for the
        // call site and this endpoint's own docstring
        // (backend src/api/visits.py::welcome_beacon) for the full legal
        // rationale.
        welcomeBeacon: async () => {
            return await this.wrap(() => this.withoutAuth().visitsApi.welcomeBeaconV1WelcomeBeaconPost());
        },
    };
    // Claims a pre-login visit's attribution signals onto the now-authenticated
    // user. Callers treat 409 (already claimed) and 404 (unknown/expired token)
    // as silent no-ops — see HttpError.status on the thrown error.
    attribution = {
        claim: async (idToken, visitToken) => {
            return await this.wrap(() => this.withAuth(idToken).attributionApi.claimAttributionV1AttributionClaimPost({
                claimAttributionRequest: { visitToken },
            }));
        },
    };
    withoutAuth() {
        return this.createApis(undefined);
    }
    withAuth(idToken) {
        return this.createApis(idToken);
    }
    createApis(idToken) {
        const configuration = new runtime.Configuration({
            basePath: this.baseUrl,
            accessToken: async () => idToken ?? "",
        });
        return {
            authentication: new AuthenticationApi(configuration),
            quota: new QuotaApi(configuration),
            sessionApi: new SessionApi(configuration),
            subscriptions: new SubscriptionsApi(configuration),
            defaultApi: new DefaultApi(configuration),
            contactApi: new ContactApi(configuration),
            visitsApi: new VisitsApi(configuration),
            attributionApi: new AttributionApi(configuration),
            purchasesApi: new PurchasesApi(configuration),
            paymentsApi: new PaymentsApi(configuration),
            cancellationApi: new CancellationApi(configuration),
        };
    }
    async normalizeError(error) {
        if (error instanceof runtime.ResponseError) {
            if (error.response.status === 401 && this.onAuthError) {
                this.onAuthError();
            }
            throw await createHttpError(error.response);
        }
        if (error instanceof runtime.FetchError) {
            throw error;
        }
        throw error instanceof Error ? error : new Error(String(error));
    }
    async wrap(fn) {
        try {
            return await fn();
        }
        catch (error) {
            return await this.normalizeError(error);
        }
    }
}
